import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DollarSign, Download, Calendar, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HRPayroll = () => {
  const { getAuthHeader } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/hr/employees`, {
        headers: getAuthHeader()
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkGenerate = async () => {
    if (!confirm(`Generate payslips for all ${employees.filter(e => e.is_active).length} active employees for ${selectedMonth}?`)) {
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/hr/payroll/generate-bulk`,
        null,
        {
          headers: getAuthHeader(),
          params: { month: selectedMonth }
        }
      );
      alert(`Successfully generated ${response.data.generated} payslips!`);
      if (response.data.failed > 0) {
        console.error('Failed payslips:', response.data.failed_details);
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to generate payslips');
    } finally {
      setGenerating(false);
    }
  };

  const handleIndividualGenerate = async (employeeId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/hr/payroll/generate`,
        { employee_id: employeeId, month: selectedMonth },
        { headers: getAuthHeader() }
      );
      alert('Payslip generated successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to generate payslip');
    }
  };

  if (loading) return <div>Loading payroll...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign size={28} />
            Payroll Management
          </h2>
          <p className="text-gray-600 mt-1">Generate and manage employee payslips</p>
        </div>
      </div>

      {/* Bulk Generation */}
      <Card className="border-primary">
        <CardHeader className="bg-primary/5">
          <CardTitle className="flex items-center gap-2">
            <Calendar size={20} />
            Bulk Payslip Generation
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2">Select Month</label>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <Button
              onClick={handleBulkGenerate}
              disabled={generating}
              className="bg-primary hover:bg-primary/90"
            >
              {generating ? 'Generating...' : `Generate for All Employees (${employees.filter(e => e.is_active).length})`}
            </Button>
          </div>
          <p className="text-sm text-gray-600 mt-3">
            ✓ Automatically calculates HRA, PF, ESI, Professional Tax, and TDS as per Indian regulations
          </p>
        </CardContent>
      </Card>

      {/* Employee List */}
      <Card>
        <CardHeader>
          <CardTitle>Generate Individual Payslips</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {employees.filter(e => e.is_active).map((employee) => (
              <div key={employee.employee_id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h4 className="font-bold">{employee.full_name}</h4>
                    <Badge variant="outline">{employee.employee_id}</Badge>
                  </div>
                  <div className="text-sm text-gray-600">
                    {employee.department} • {employee.designation}
                  </div>
                  <div className="text-sm font-medium text-primary mt-1">
                    Base Salary: ₹{employee.salary.toLocaleString()}/month
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleIndividualGenerate(employee.employee_id)}
                  variant="outline"
                >
                  Generate for {selectedMonth}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <h4 className="font-semibold mb-2 flex items-center gap-2">
            <CheckCircle className="text-blue-600" size={20} />
            Indian Payroll Compliance
          </h4>
          <ul className="text-sm text-gray-700 space-y-1">
            <li>• HRA: 40% of basic salary (metro cities)</li>
            <li>• PF: 12% employee contribution (auto-calculated)</li>
            <li>• ESI: 0.75% of gross (if salary &lt; ₹21,000)</li>
            <li>• Professional Tax: As per Gujarat state regulations</li>
            <li>• TDS: Based on annual income and tax slabs</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default HRPayroll;